// DossierDetail — Fiche dossier redesignée
// Header fixe + Pipeline 9 étapes + 4 onglets contextuels
import { useState, useEffect, useRef, useCallback } from "react";
import { isInstallateurProfile, hasDossierAmo, hasVisitesTerrain, hasDossierSite } from "../../utils/tenantProfile";
import { DossierArtisanView } from "./artisan/DossierArtisanView";
import {
  ArrowLeft, RefreshCw, Loader2, Clock, Shield, MapPin, Banknote,
  Briefcase, Calculator, Building2, Thermometer, FileText, AlertTriangle,
  FolderOpen, Lock, CheckCircle2, XCircle, AlertCircle, HelpCircle,
  ChevronDown, ArrowRight, Info, Trash2, Copy, Check, Send,
  HardHat, MessageSquare, Files, Users, CalendarDays, Wrench, FileSignature, CreditCard,
  HandCoins, ExternalLink,
  ClipboardList,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

// Internal components
import { PipelineStepper, getPipelineStep, getPipelineOrder, isAbandonne } from "./dossier/PipelineStepper";
import DatesPipelineBlock from "./dossier/DatesPipelineBlock";
import { GovernanceIndicator } from "./dossier/GovernanceIndicator";
import { InfosTab } from "./dossier/InfosTab";
import { ACompleterBlock } from "./dossier/ACompleterBlock";
import { DALMobileBlock } from "./dossier/DALMobileBlock";
import { CommercialSection } from "./dossier/CommercialSection";
import { OperationSection } from "./dossier/OperationSection";
import { ConformiteEditableTab } from "./dossier/ConformiteEditableTab";
import { PlanFinancementBlock } from "./dossier/PlanFinancementBlock";
import { FinanceSection } from "./dossier/FinanceSection";
import { FinancementTab } from "./dossier/FinancementTab";
import { AidesComplementairesTab } from "./dossier/AidesComplementairesTab";
import { DocumentsForm } from "./dossier/DocumentsForm";
import { HistoriqueModal } from "./dossier/HistoriqueModal";
import SendMessageModal from "./dossier/SendMessageModal";
import { CommentairesDossier } from "./dossier/CommentairesDossier";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { TutoModal } from "../../components/TutoModal";
import { DossierInstallateurView } from "./dossier/DossierInstallateurView";
import { DossierVisiteurView } from "./dossier/DossierVisiteurView";
import { API, getAuthHeaders, STATUTS_CONFIG, etatFactureAMO } from "./dossier/helpers";

// External components
import PhotosChantier from "./PhotosChantier";
import { PhotosForm } from "./dossier/PhotosForm";
import CommandesManager from "../operation/CommandesManager";
import PlanningChantier from "../operation/PlanningChantier";
import AnahLinkTab from "../../components/metier/AnahLinkTab";
import DALForm from "../../components/tef/DALForm";
import DALConsultation from "../../components/tef/DALConsultation";
import { DesktopDALWizard } from "./dossier/DesktopDALWizard";
import { SplitViewLayout } from "./dossier/SplitViewLayout";
import DossierOmvView from "../../components/omv/DossierOmvView";
import { formatClientFullName } from "../../utils/clientFormat";

// ─── Onglets config ───
// Refonte P1.1 — ordre process TEF : Infos · Demande MPA · DAL · Finance · Conformité.
// La clé interne "operation" est CONSERVÉE (routage HUB_TO_TAB / getInitialTab /
// headerAction / complétude) ; seul le LABEL devient "Demande MPA".
const TABS = [
  { key: "infos", label: "Infos", icon: Info, color: "var(--oh-hub-commercial)" },
  { key: "operation", label: "Demande MPA", labelCore: "Opération", icon: Briefcase, color: "var(--oh-hub-operation)" },
  { key: "visites", label: "Visites", icon: ClipboardList, color: "var(--oh-hub-operation)" },
  { key: "dal", label: "DAL", icon: FileText, color: "var(--oh-stat-orange-text, #fb923c)" },
  { key: "finance", label: "Finance", icon: Banknote, color: "var(--oh-hub-finance, #10b981)" },
  { key: "conformite", label: "Conformité", icon: Shield, color: "var(--oh-hub-conformité, var(--oh-stat-amber-text))" },
];

// ─── Split View (prototype design JJ) — menus master-detail intra-onglet ───
// Les `id` correspondent aux `data-subnav-id` DÉJÀ présents dans les composants
// de section existants (CommercialSection / OperationSection). On NE recrée rien :
// SplitViewLayout affiche à droite le bloc dont l'id est sélectionné à gauche.
const SPLIT_SECTIONS_OPERATION = [
  { id: "contrats",      label: "Contrats & Engagement", icon: FileSignature, color: "var(--oh-hub-commercial)" },
  { id: "visiteur",      label: "Visiteur",              icon: Users,         color: "var(--oh-stat-blue-text)" },
  { id: "visite",        label: "Planning RDV",          icon: CalendarDays,  color: "var(--oh-hub-operation)" },
  { id: "installateur",  label: "Installateur",          icon: Wrench,        color: "var(--oh-stat-blue-text)" },
  { id: "prestation-amo",label: "Prestation",            icon: Briefcase,     color: "var(--oh-hub-commercial)" },
  { id: "chantier",      label: "Chantier",              icon: Building2,     color: "var(--oh-hub-operation)" },
  { id: "financement",   label: "Financement",           icon: Banknote,      color: "var(--oh-hub-finance, #10b981)" },
];
const SPLIT_SECTIONS_DAL = [
  { id: "dal-mobile",    label: "DAL Mobile",            icon: FileText,      color: "var(--oh-stat-orange-text, #fb923c)" },
  { id: "dal-wizard",    label: "Assistant DAL",         icon: FileText,      color: "var(--oh-stat-orange-text, #fb923c)" },
  { id: "dal-classique", label: "DAL",                   icon: Files,         color: "var(--oh-text-muted)" },
];
// Onglet Infos — 4 blocs EXISTANTS de InfosTab.jsx (ancrés data-subnav-id).
const SPLIT_SECTIONS_INFOS = [
  { id: "beneficiaire",  label: "Bénéficiaire",          icon: Users,         color: "var(--oh-hub-commercial)" },
  { id: "logement",      label: "Logement",              icon: MapPin,        color: "var(--oh-hub-operation)" },
  { id: "mpa",           label: "MaPrimeAdapt'",         icon: Calculator,    color: "var(--oh-stat-amber-text)" },
  { id: "compte-anah",   label: "Compte ANAH",           icon: Lock,          color: "var(--oh-stat-blue-text)" },
];
// Onglet Finance — 3 blocs EXISTANTS de FinanceSection.jsx (déjà ancrés).
const SPLIT_SECTIONS_FINANCE = [
  { id: "facture-amo",       label: "Facture AMO",       icon: Banknote,      color: "var(--oh-hub-finance, #10b981)" },
  { id: "paiement",          label: "Règlements AMO",    icon: CreditCard,    color: "var(--oh-hub-finance, #10b981)" },
  { id: "paiement-visiteur", label: "Visite payée",      icon: Users,         color: "var(--oh-stat-amber-text)" },
];
// Onglet Conformité — 2 blocs cohérents : trigger Liaison ANAH + carte Vérification
// (ConformiteEditableTab garde son header/save/progress + accordéons internes).
const SPLIT_SECTIONS_CONFORMITE = [
  { id: "liaison-anah",        label: "Liaison ANAH",          icon: Shield,        color: "var(--oh-stat-green-text, #10b981)" },
  { id: "plan-financement",    label: "Plan de financement",   icon: Calculator,    color: "var(--oh-stat-purple-text)" },
  { id: "aides-complementaires", label: "Aides complémentaires", icon: HandCoins,   color: "var(--oh-hub-aides, var(--oh-stat-cyan-text))" },
  { id: "verification",        label: "Vérification",          icon: CheckCircle2,  color: "var(--oh-stat-amber-text)" },
];

// Mapping hub d'origine → onglet par défaut
const HUB_TO_TAB = {
  commercial: "infos",
  operation: "operation",
  conformite: "conformite",
  conformité: "conformite",
  aides: "finance",
  finance: "finance",
};

// ─── Compteur complétude par onglet ───
function computeTabCompletude(dossier) {
  if (!dossier) return {};
  const c = dossier.client || {};
  const l = dossier.logement || {};
  const wf = dossier.workflow_data?.tef || {};

  const check = (v) => v !== undefined && v !== null && v !== "" && v !== false;

  // Refonte P1 #3 : chaque champ porte un LABEL → on expose la liste `missing`
  // (ce qui manque) par onglet, sans changer les critères ni le total existants,
  // et SANS toucher au calcul du statut pipeline (verrou §B#3).
  const mk = (fields) => {
    const missing = fields.filter(f => !check(f.v)).map(f => f.label);
    return { filled: fields.length - missing.length, total: fields.length, missing };
  };

  const dal = wf.infos_dal || {};
  const dalTravaux = !!(dal.travaux_preconises && Object.keys(dal.travaux_preconises).length > 0);
  const dalZones = !!(dal.zones_mobile && Object.keys(dal.zones_mobile).length > 0);

  return {
    // Infos: client + logement essentials
    infos: mk([
      { label: "Nom", v: c.nom },
      { label: "Prénom", v: c.prenom },
      { label: "Téléphone", v: c.telephone },
      { label: "Email", v: c.email },
      { label: "Adresse", v: l.adresse || l.code_postal },
      { label: "Type logement", v: l.type_logement },
      { label: "Statut occupation", v: l.statut_occupation },
      { label: "Surface habitable", v: l.surface_habitable },
    ]),
    // Opération: intervenants + visite + projet
    operation: mk([
      { label: "Visiteur AMO", v: dossier.visiteur_amo },
      { label: "Installateur", v: dossier.installateur_nom },
      { label: "Date de visite", v: dossier.date_visite },
      { label: "Contrat signé", v: dossier.contrat_signe },
      { label: "Type de projet", v: dossier.type_projet },
    ]),
    // DAL (onglet dédié) — présence des éléments terrain
    dal: mk([
      { label: "Travaux préconisés", v: dalTravaux },
      { label: "Zones (relevé mobile)", v: dalZones },
    ]),
    // Conformité: DAL + ANAH + dépôt
    conformite: mk([
      { label: "Travaux préconisés (DAL)", v: dalTravaux },
      { label: "Zones DAL", v: dalZones },
      { label: "Référence ANAH", v: dossier.reference_anah },
      { label: "Liaison ANAH", v: dossier.anah_linked },
      { label: "Date dépôt ANAH", v: dossier.date_depot_anah },
      { label: "Statut ANAH", v: dossier.statut_anah },
      { label: "Date d'octroi", v: dossier.date_octroi },
    ]),
    // Finance TEF (revenus AMO uniquement — jamais de subvention ANAH ici).
    // ⚠️ #569 — l'état vient du BACKEND, il n'est plus recalculé ici. Les trois
    // états sont rendus TELS QUELS : « pas de facture » n'est pas un impayé, et
    // « inconnu » n'est pas « non payée ».
    finance: mk([
      { label: "N° facture AMO", v: dossier.reference_facture_amo },
      { label: "Facture AMO", v: {
        payee: "Payée",
        non_payee: "Non payée — à relancer",
        sans_facture: "Aucune facture émise",
      }[etatFactureAMO(dossier)] || "État inconnu" },
      // Le motif dit POURQUOI ; un état financier nu se conteste, un état motivé
      // se vérifie. Et la fraîcheur se lit À CÔTÉ du chiffre, jamais à sa place.
      { label: "Motif", v: dossier.facture_payee_motif },
      { label: "Synchro Pennylane", v: dossier.facture_snapshot_frais === false
        ? `⚠️ snapshot non rafraîchi (${dossier.facture_synchro_le || "date inconnue"})`
        : dossier.facture_synchro_le },
      ...(dossier.facture_divergences || []).map((d) => ({
        label: "⚠️ Divergence", v: d,
      })),
    ]),
  };
}

// ─── Main Component ───
export default function DossierDetail({ dossierId, tenantId, onBack, onNavigate, hubContext }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [analyzingAgent, setAnalyzingAgent] = useState(false);
  const [agentProgress, setAgentProgress] = useState(null);
  const [blockedTransitions, setBlockedTransitions] = useState([]);
  const [showHistorique, setShowHistorique] = useState(false);
  const [showTuto, setShowTuto] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(false);
  // Refonte P1.1 — panneaux Documents & Commentaires exposés depuis le header (actions nommées)
  const [showDocuments, setShowDocuments] = useState(false);
  const [showCommentaires, setShowCommentaires] = useState(false);
  // Conformité — Liaison ANAH (données importées) + Plan financement CERFA en MODALE.
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [syncingZoho, setSyncingZoho] = useState(false);
  const initialLoadDone = useRef(false);
  // Navigation programmatique du split DAL (Consultation « Modifier » → « Assistant DAL »).
  const dalNavRef = useRef(null);

  const handleResyncZoho = useCallback(async () => {
    if (!dossierId || syncingZoho) return;
    setSyncingZoho(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const res = await fetch(
        `${API}/api/metier/tef/zoho/resync-dossier/${dossierId}?push_to_zoho=false`,
        { method: "POST", headers, credentials: "include" }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      const fz = data.from_zoho_documents || {};
      const fp = data.from_zoho_photos || {};
      const imp = (fz.imported || 0) + (fp.imported || 0);
      const lnk = (fz.linked || 0) + (fp.linked || 0);
      const skp = (fz.skipped || 0) + (fp.skipped || 0);
      toast.success(`Sync Zoho OK — ${imp} importé(s), ${lnk} lié(s), ${skp} déjà à jour`);
      loadDossier();
    } catch (e) {
      toast.error(`Sync Zoho échoué : ${e.message}`);
    } finally {
      setSyncingZoho(false);
    }
  }, [dossierId, tenantId, syncingZoho]);

  // Active tab — from URL ?tab= or hubContext or default
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && TABS.find(t => t.key === tabParam)) return tabParam;
    if (hubContext && HUB_TO_TAB[hubContext]) return HUB_TO_TAB[hubContext];
    return "infos";
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [refCopied, setRefCopied] = useState(false);
  const copyRef = useCallback(async () => {
    const ref = dossier?.reference || dossier?.référence;
    if (!ref) return;
    try {
      await navigator.clipboard.writeText(ref);
      setRefCopied(true);
      toast.success(`Référence ${ref} copiée`);
      setTimeout(() => setRefCopied(false), 1500);
    } catch {
      toast.error("Impossible de copier");
    }
  }, [dossier]);

  // Role detection
  const userRole = user?.role || user?.rôle || "user";
  const isTEFRole = ["admin", "super_admin", "manager"].includes(userRole) || user?.is_super_admin;
  const isInstallateurRole = userRole === "external" || userRole === "installateur";
  const isVisiteurRole = userRole === "visiteur";
  const effectiveRole = isTEFRole ? "tef" : isInstallateurRole ? "installateur" : isVisiteurRole ? "visiteur" : "tef";

  // Tabs visible by role. L'onglet DAL est en plus gaté par TENANT (TEF only) :
  // sa présence dans la barre suivait le rôle alors que son panneau suit isTEF
  // (l.362), d'où un onglet vide chez les admins non-TEF (OMV/Solène, climatpac).
  // dossierIsTEF = miroir null-safe d'isTEF (visibleTabs s'exécute avant le guard).
  const dossierIsTEF = dossier?.activite === "tef" || tenantId === "tef";

  // 🔴 LA COMPOSITION MÉTIER VIENT DE LA CAPACITÉ DÉCLARÉE, ⛔ PLUS DU RÔLE.
  //
  // ⚠️ CE QUI SE PASSAIT, mesuré le 18/09 : `effectiveRole` valait "tef" dès que
  // l'utilisateur était admin (`isTEFRole` = un nom qui MENT, il teste
  // ["admin","super_admin","manager"]), ET la ternaire retombait sur "tef" en
  // dernier repli. Un dossier `mario_bat` / `activite=renovation` / profil CORE
  // recevait donc MaPrimeAdapt', Compte ANAH, DAL, Finance AMO et Conformité
  // ANAH — parce que celui qui le REGARDAIT était administrateur.
  // 🔑 LE RÔLE GOUVERNE LES DROITS ; LA CAPACITÉ GOUVERNE LA COMPOSITION.
  // ⛔ Absence de déclaration ⛔ n'est PAS permission d'hériter de TEF.
  const amo = hasDossierAmo();
  // 🔴 L'ONGLET « VISITES » SUIT LA CAPACITÉ TERRAIN — même méthode que l'AMO.
  // ⚠️ La relation dossier↔visite EXISTE en base depuis toujours
  // (`saisie.py:119`) ; ⛔ AUCUN écran ⛔ ne permettait de la parcourir. On
  // EXPOSE, ⛔ on ⛔ ne crée RIEN.
  const terrain = hasVisitesTerrain();
  const TABS_DECLARES = TABS.filter(
    t => (amo || !["dal", "finance", "conformite"].includes(t.key))
         && (t.key !== "visites" || terrain));
  // ⚠️ Le filtre par RÔLE reste — mais il ne porte plus que sur l'ÉTENDUE de ce
  // qu'un profil restreint (installateur, visiteur) a le droit de voir, ⛔ plus
  // sur QUELS MÉTIERS existent.
  const visibleTabs = (effectiveRole === "tef"
    ? TABS_DECLARES
    : TABS_DECLARES.filter(t => ["infos", "operation", "conformite"].includes(t.key))
  ).filter(t => t.key !== "dal" || dossierIsTEF);

  useEffect(() => { if (dossierId) loadDossier(); }, [dossierId]);

  const loadDossier = async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const headers = getAuthHeaders(tenantId);
      const [res, docsRes, histRes] = await Promise.all([
        fetch(`${API}/api/metier/dossiers/${dossierId}`, { headers, credentials: "include" }),
        fetch(`${API}/api/metier/dossiers/${dossierId}/documents`, { headers, credentials: "include" }),
        fetch(`${API}/api/metier/dossiers/${dossierId}/historique`, { headers, credentials: "include" }),
      ]);
      let dossierData = null;
      if (res.ok) { dossierData = await res.json(); setDossier(dossierData); }
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (histRes.ok) setHistorique(await histRes.json());
      if (dossierData?.client_id && !dossierData?.client_nom) {
        try {
          const clientRes = await fetch(`${API}/api/metier/clients/${dossierData.client_id}`, { headers, credentials: "include" });
          if (clientRes.ok) {
            const cd = await clientRes.json();
            setDossier(prev => prev ? { ...prev, client_nom: formatClientFullName(cd) } : prev);
          }
        } catch (e) { console.warn("Client load error:", e); }
      }
    } catch (err) { console.error("Erreur chargement dossier:", err); }
    finally { setLoading(false); initialLoadDone.current = true; }
  };

  const handleLaunchAgent = async () => {
    if (!dossier?.client_id) { toast.error("Pas de client_id associé au dossier"); return; }
    setAnalyzingAgent(true);
    setAgentProgress("Lancement de l'analyse...");
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/conformite/agent/analyze/${dossier.client_id}`, { method: "POST", headers, credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const taskId = data.task_id;
        toast.success("Analyse lancée...");
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${API}/api/conformite/agent/task-status/${taskId}`, { headers, credentials: "include" });
            if (statusRes.ok) {
              const status = await statusRes.json();
              if (status.status === "completed") {
                clearInterval(pollInterval);
                setAnalyzingAgent(false); setAgentProgress(null);
                toast.success(`Analyse terminée : ${[...(status.fields_updated_client || []), ...(status.fields_updated_dossier || [])].length} champs mis à jour`);
                loadDossier();
              } else if (status.status === "error") {
                clearInterval(pollInterval);
                setAnalyzingAgent(false); setAgentProgress(null);
                toast.error(status.error || "Erreur");
              } else { setAgentProgress(`Analyse en cours... (${status.phase || "extraction"})`); }
            }
          } catch {}
        }, 3000);
        setTimeout(() => { clearInterval(pollInterval); setAnalyzingAgent(false); setAgentProgress(null); loadDossier(); }, 300000);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lancement agent");
        setAnalyzingAgent(false); setAgentProgress(null);
      }
    } catch { toast.error("Erreur réseau"); setAnalyzingAgent(false); setAgentProgress(null); }
  };

  // Delete dossier handler (admin only)
  const handleDeleteDossier = async () => {
    const ref = dossier?.référence || dossier?.reference || dossierId;
    const confirmed = window.prompt(`Pour confirmer la suppression du dossier ${ref}, tapez "SUPPRIMER"`);
    if (confirmed !== "SUPPRIMER") return;
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}?force=true`, {
        method: "DELETE",
        headers: { ...(localStorage.getItem("session_token") && { Authorization: `Bearer ${localStorage.getItem("session_token")}` }), "X-Tenant-ID": tenantId || "tef" },
        credentials: "include",
      });
      if (res.ok) { toast.success("Dossier supprimé"); onBack?.(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Erreur suppression"); }
    } catch { toast.error("Erreur réseau"); }
  };

  // Statut change handler
  const handleStatutChange = async (newStatut, raison) => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossier.id || dossierId}/statut`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("session_token") && { Authorization: `Bearer ${localStorage.getItem("session_token")}` }) },
        credentials: "include",
        body: JSON.stringify(raison ? { statut: newStatut, raison } : { statut: newStatut })
      });
      if (res.ok) { toast.success(`Statut mis à jour`); loadDossier(); }
      else { const e = await res.json(); toast.error(e.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
  };

  // OH-0012 — bascule HUMAINE Actif/Inactif (indépendante du statut). Un dossier
  // "Inactif" matérialise une décision externe (ex. annulation DDT) que l'ANAH ne
  // reflète pas : il est GELÉ par la synchro (statut non écrasé, non réenrichi).
  const [togglingActif, setTogglingActif] = useState(false);
  const handleToggleActif = async () => {
    const next = (dossier.actif_inactif === "Inactif") ? "Actif" : "Inactif";
    setTogglingActif(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossier.id || dossierId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("session_token") && { Authorization: `Bearer ${localStorage.getItem("session_token")}` }) },
        credentials: "include",
        body: JSON.stringify({ actif_inactif: next }),
      });
      if (res.ok) { toast.success(next === "Inactif" ? "Dossier marqué Inactif (gelé pour la synchro ANAH)" : "Dossier réactivé"); loadDossier(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setTogglingActif(false); }
  };

  // ─── Loading / Error ───
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
        <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Chargement du dossier...</p>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-[var(--oh-error-text)] mb-4" />
        <p style={{ color: "var(--oh-text)" }}>Dossier non trouvé</p>
        <div className="flex gap-2 justify-center mt-4">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
          <Button variant="outline" onClick={loadDossier}><RefreshCw className="w-4 h-4 mr-2" /> Réessayer</Button>
        </div>
      </div>
    );
  }

  // ─── VUES DÉDIÉES PAR RÔLE ───
  if (effectiveRole === "installateur") {
    return <DossierInstallateurView dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents} onBack={onBack} onUpdate={loadDossier} />;
  }
  if (effectiveRole === "visiteur") {
    return <DossierVisiteurView dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents} onBack={onBack} onUpdate={loadDossier} />;
  }

  // ─── RÉSOLUTION TENANT EXPLICITE — plus jamais de défaut TEF implicite ───
  // Un dossier mal scopé (tenant_id absent ET contexte non résolu) doit être VISIBLE
  // (état vide explicite), pas déguisé en fiche TEF. Signaux par ordre d'autorité :
  //   1. dossier.tenant_id  (stampé en base — autoritaire ; cf. fix stamp omv_france Dev 2)
  //   2. tenantId           (tenant actif = currentEntreprise.slug)
  //   3. current_tenant_slug (même source que HubGrid — évite la désync prop/localStorage)
  let resolvedTenant = dossier.tenant_id || tenantId || null;
  if (!resolvedTenant && typeof window !== "undefined") {
    try { resolvedTenant = localStorage.getItem("current_tenant_slug") || null; } catch { /* noop */ }
  }

  // Profil installateur (OMV, Climat) → fiche CEE dédiée (DossierOmvView).
  if (isInstallateurProfile()) {
    return (
      <DossierOmvView
        dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents}
        historique={historique}
        onBack={onBack} onUpdate={loadDossier} onNavigate={onNavigate}
        onStatutChange={handleStatutChange} onDelete={isTEFRole ? handleDeleteDossier : undefined}
        isTEFRole={isTEFRole}
      />
    );
  }

  // 🔴 L2 (N°876) — un tenant qui a ADOPTÉ le modèle Site (un dossier = 1 client
  // + 1 site) reçoit la FICHE ARTISAN dédiée, ⛔ plus la fiche TEF filtrée par
  // gates. ⛔ Aucun composant du DAL gelé n'y est branché. TEF, OMV et Climat
  // ne déclarent pas `dossier_site_requis` ⇒ ⛔ inchangés.
  if (hasDossierSite()) {
    return (
      <DossierArtisanView
        dossier={dossier} dossierId={dossierId} tenantId={tenantId} documents={documents}
        historique={historique}
        onBack={onBack} onUpdate={loadDossier} onNavigate={onNavigate}
      />
    );
  }

  // 🔴 LA GARDE VÉRIFIE LE RATTACHEMENT, ⛔ PLUS UNE LISTE DE SLUGS.
  //
  // ⚠️ CE QU'ELLE FAISAIT, mesuré le 2026-09-17 : elle comparait le tenant à
  // ["tef", "climatpac", "demo"] — une LISTE BLANCHE EN DUR, dont le
  // commentaire disait « ajuster la liste si besoin ». Résultat : JJ crée
  // MARI-DOS-2026-00003, le document est PARFAITEMENT rattaché
  // (tenant_id: "mario_bat"), et la fiche REFUSE de s'afficher — au motif
  // « non rattaché à un tenant reconnu », qui est FAUX.
  // ⛔ Sans fiche : pas de visite, pas de dictée, pas de devis.
  //
  // 🔑 CE QUE LA GARDE DOIT VRAIMENT EMPÊCHER, et qu'elle empêche TOUJOURS :
  // qu'un dossier SANS tenant résolu soit déguisé en fiche TEF. Ça se teste
  // sur l'ABSENCE de rattachement — ⛔ pas sur l'appartenance à une liste que
  // chaque nouveau tenant oblige à rééditer. Les profils ayant une fiche
  // DIFFÉRENTE (OMV/Climat) sont déjà aiguillés AU-DESSUS.
  // ⚠️ Même famille que `!isOmv`, en miroir : la liste était POSITIVE et
  // FERMÉE, donc elle excluait en silence tout arrivant. ⛔ On ⛔ n'ajoute PAS
  // « mario_bat » : le tenant suivant retomberait dans le même mur.
  if (!resolvedTenant) {
    return (
      <div className="text-center py-12" data-testid="dossier-tenant-inconnu">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--oh-stat-amber-text)" }} />
        <p className="font-medium" style={{ color: "var(--oh-text)" }}>Dossier non rattaché à un tenant reconnu</p>
        <p className="text-sm mt-1 max-w-md mx-auto" style={{ color: "var(--oh-text-muted)" }}>
          Tenant résolu : <strong>{resolvedTenant || "aucun"}</strong> — fiche non affichée par sécurité
          (pas de repli TEF). Vérifier le rattachement du dossier{" "}
          <strong>{dossier.reference || dossier.référence || dossierId}</strong>.
        </p>
        <div className="flex gap-2 justify-center mt-4">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
          <Button variant="outline" onClick={loadDossier}><RefreshCw className="w-4 h-4 mr-2" /> Réessayer</Button>
        </div>
      </div>
    );
  }

  // ─── VUE GÉNÉRIQUE (tenant explicite : tef / climatpac / demo) — inchangée ───
  const statutsMap = STATUTS_CONFIG[dossier.activite] || STATUTS_CONFIG.tef;
  const abandoned = isAbandonne(dossier);
  const pipelineStep = getPipelineStep(dossier.statut, dossier);
  const pipelineOrder = getPipelineOrder(dossier.statut, dossier);

  const statutInfo = abandoned
    ? { label: "Abandonné", color: "bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)] text-[var(--oh-error-text)] dark:text-[var(--oh-error-text)]", icon: AlertTriangle }
    : (statutsMap[dossier.statut] || { label: dossier.statut, color: "bg-zinc-500/20 border border-[var(--oh-border)] text-[var(--oh-text)] dark:text-[var(--oh-text-secondary)]", icon: FileText });
  const StatusIcon = statutInfo.icon;
  const isTEF = dossier.activite === "tef" || tenantId === "tef";
  const completudePct = dossier.completude_agent?.global_pct || 0;
  const tabCompletude = computeTabCompletude(dossier);
  const dossierRef = dossier.reference || dossier.référence || "";
  // P0-04 fix BUG-11 (2026-05-03) — formatClientFullName gere aussi le cas B2B (raison_sociale)
  const clientDisplay = formatClientFullName(dossier.client) || dossier.client_nom || "—";
  // Refonte P1.1 — méta header condensé : ville · visiteur · installateur (mis en avant).
  // Mêmes sources que AdminBlock (adresse_projet / visiteur_amo / installateur_nom),
  // pas de nouvelle source ni de hard code (O1).
  const headerVille = dossier.adresse_projet?.ville || dossier.logement?.ville || dossier.client?.adresse?.ville || "";
  const headerCP = dossier.adresse_projet?.code_postal || dossier.logement?.code_postal || dossier.client?.adresse?.code_postal || "";
  // BAT JJ — ville AVEC code postal (ex. "Bobigny 93000"), mêmes sources que AdminBlock.
  const headerLocalite = [headerVille, headerCP].filter(Boolean).join(" ");
  const headerVisiteur = dossier.visiteur_amo || "";
  const headerInstallateur = dossier.installateur_nom || "";
  // Indicateur conformité = complétude de l'onglet Conformité déjà calculée (réutilisée, pas recalculée).
  const headerConf = tabCompletude.conformite;

  // Header action contextuelle
  const headerAction = abandoned ? null : (() => {
    if (pipelineStep === "dossier_cree") return { label: "Planifier visite", tab: "operation" };
    if (pipelineStep === "visite_planifiee") return { label: "Envoyer accès DAL", tab: "operation" };
    if (pipelineStep === "visite_faite") return { label: "Finaliser DAL", tab: "conformite" };
    // Étapes ANAH — libellés non impératifs : OmniHub PRÉPARE/SUIT, le dépôt réel
    // se fait sur le portail officiel monprojet.anah.gouv.fr (seule étape externe,
    // assumée via l'action « Continuer sur le portail officiel ANAH »).
    if (pipelineStep === "pret_depot") return { label: "Préparer le dépôt ANAH", tab: "conformite", anahPortal: true };
    if (pipelineStep === "depose") return { label: "Suivi de l'instruction", tab: "conformite", anahPortal: true };
    if (pipelineStep === "instruction") return { label: "État du dossier ANAH", tab: "conformite", anahPortal: true };
    if (pipelineStep === "octroi") return { label: "Planifier travaux", tab: "operation" };
    if (pipelineStep === "travaux") return { label: "Suivre travaux", tab: "operation" };
    return null;
  })();

  return (
    <div className="space-y-0" data-testid="dossier-detail">

      {/* ═══ HEADER STICKY ═══ */}
      <div
        className="sticky top-0 z-40 -mx-4 sm:-mx-0 backdrop-blur-xl"
        style={{ background: "var(--oh-bg-elevated)", borderBottom: "1px solid var(--oh-border)" }}
        data-testid="dossier-header"
      >
        {/* Row 1: Nav + Client + Statut + Complétude + Actions */}
        <div className="flex items-center gap-2 sm:gap-3 px-4 py-2.5 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn" className="p-1.5">
            <ArrowLeft className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
          </Button>

          {/* Client name */}
          <div className="flex items-center gap-2 min-w-0">
            <div className={`p-1 rounded ${isTEF ? "bg-[var(--oh-accent-bg)]" : "bg-[var(--oh-info-bg)]"}`}>
              {isTEF ? <Building2 className="w-3.5 h-3.5 text-[var(--oh-accent-text)]" /> : <Thermometer className="w-3.5 h-3.5 text-[var(--oh-info-text)]" />}
            </div>
            <div className="min-w-0">
              {/* Header principal : REF — NOM (demande JJ 2026-04-21 pour
                  appels ANAH/client où la ref est la clé de recherche) */}
              <p className="text-sm font-medium truncate flex items-center gap-1.5" style={{ color: "var(--oh-text)" }} data-testid="dossier-header-title">
                {dossierRef && (
                  <>
                    <span className="font-mono" style={{ color: "var(--oh-text-muted)" }}>
                      {dossierRef}
                    </span>
                    <button
                      onClick={copyRef}
                      className="p-0.5 rounded hover:bg-[var(--oh-bg-elevated)] transition-colors"
                      title={refCopied ? "Copié !" : "Copier la référence"}
                      data-testid="copy-ref-btn"
                    >
                      {refCopied
                        ? <Check className="w-3 h-3 text-[var(--oh-success-text)]" />
                        : <Copy className="w-3 h-3" style={{ color: "var(--oh-text-muted)" }} />}
                    </button>
                    <span style={{ color: "var(--oh-text-muted)" }}>— </span>
                  </>
                )}
                {clientDisplay}
              </p>
              {/* Refonte P1.1 — méta condensée : ville · visiteur · INSTALLATEUR (mis en avant) */}
              <div className="hidden sm:flex items-center gap-2 text-[11px] mt-0.5 flex-wrap" data-testid="dossier-header-meta" style={{ color: "var(--oh-text-muted)" }}>
                {headerLocalite && (
                  <span className="inline-flex items-center gap-1" data-testid="header-ville">
                    <MapPin className="w-3 h-3" /> {headerLocalite}
                  </span>
                )}
                {headerVisiteur && (
                  <span className="inline-flex items-center gap-1" data-testid="header-visiteur">
                    <span style={{ opacity: 0.5 }}>·</span> <Briefcase className="w-3 h-3" /> {headerVisiteur}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span
                    className="inline-flex items-center gap-1 font-medium px-1.5 py-0.5 rounded"
                    data-testid="header-installateur"
                    title={headerInstallateur ? `Installateur : ${headerInstallateur}` : "Aucun installateur assigné"}
                    style={headerInstallateur
                      ? { background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)", border: "1px solid var(--oh-stat-blue-border)" }
                      : { color: "var(--oh-text-muted)", border: "1px dashed var(--oh-border)" }}
                  >
                    <HardHat className="w-3 h-3" /> {headerInstallateur || "Installateur —"}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Statut dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 cursor-pointer hover:opacity-80" data-testid="statut-dropdown-trigger">
                <Badge className={`${statutInfo.color} px-2 py-0.5 text-xs`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statutInfo.label}
                  <ChevronDown className="w-3 h-3 ml-1 opacity-50" />
                </Badge>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              {[
                { id: "prospect", label: "Prospect" },
                { id: "dossier_cree", label: "Dossier créé" },
                { id: "visite_planifiee", label: "Visite planifiée" },
                { id: "visite_faite", label: "Visite faite" },
                { id: "dossier_depose", label: "Déposé ANAH" },
                { id: "aides_en_instruction", label: "En instruction" },
                { id: "aides_validees", label: "Aides validées" },
                { id: "travaux_en_cours", label: "Travaux en cours" },
                { id: "travaux_termines", label: "Travaux terminés" },
                { id: "paye", label: "Payé" },
              ].map(s => (
                <DropdownMenuItem key={s.id} disabled={dossier.statut === s.id} className={dossier.statut === s.id ? "font-medium" : ""}
                  onClick={() => handleStatutChange(s.id)}>
                  {s.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {[
                { id: "abandonne", label: "Abandonné" },
                { id: "refuse_anah", label: "Refusé ANAH" },
                { id: "archive", label: "Archivé" },
              ].map(s => (
                <DropdownMenuItem key={s.id} className="text-[var(--oh-error-text)]" disabled={dossier.statut === s.id}
                  onClick={() => { const r = prompt(`Raison pour "${s.label}" ?`); if (r !== null) handleStatutChange(s.id, r); }}>
                  {s.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Complétude */}
          {completudePct > 0 && (
            <div className="hidden sm:flex items-center gap-1.5" data-testid="completude-header">
              <div className="w-16 h-1.5 rounded-full" style={{ background: "var(--oh-border)" }}>
                <div className="h-full rounded-full transition-all" style={{
                  width: `${completudePct}%`,
                  background: completudePct >= 80 ? "#10b981" : completudePct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)",
                }} />
              </div>
              <span className="text-[10px] font-medium" style={{
                color: completudePct >= 80 ? "#10b981" : completudePct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)",
              }}>{completudePct}%</span>
            </div>
          )}

          {/* Refonte P1.1 — indicateur conformité (réutilise la complétude de l'onglet Conformité) */}
          {headerConf && (
            <div
              className="hidden sm:flex items-center gap-1"
              data-testid="conformite-header"
              title={headerConf.missing?.length ? `Conformité à compléter : ${headerConf.missing.join(", ")}` : "Conformité complète ✓"}
            >
              <Shield className="w-3 h-3" style={{ color: headerConf.filled === headerConf.total ? "#10b981" : "var(--oh-stat-amber-text)" }} />
              <span className="text-[10px] font-medium" style={{ color: headerConf.filled === headerConf.total ? "#10b981" : "var(--oh-stat-amber-text)" }}>
                {headerConf.filled}/{headerConf.total}
              </span>
            </div>
          )}

          {dossier.verrouille && (
            <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] border-[var(--oh-error-border)] text-[10px]"><Lock className="w-3 h-3 mr-0.5" />Verrouillé</Badge>
          )}

          {/* OH-0012 — toggle Actif/Inactif (décision humaine, gèle la synchro ANAH) */}
          {isTEFRole && (() => {
            const inactif = dossier.actif_inactif === "Inactif";
            return (
              <button
                data-testid="toggle-actif-inactif"
                onClick={handleToggleActif}
                disabled={togglingActif}
                aria-label={inactif ? "Réactiver le dossier" : "Marquer le dossier Inactif (gel synchro ANAH)"}
                title={inactif
                  ? "Dossier INACTIF — gelé : la synchro ANAH ne l'écrase plus. Cliquer pour réactiver."
                  : "Dossier actif. Cliquer pour marquer Inactif (décision externe type annulation DDT) — il sera gelé côté synchro ANAH."}
                className="text-[10px] font-medium rounded px-2 py-0.5 inline-flex items-center gap-1"
                style={inactif
                  ? { background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "1px solid var(--oh-stat-amber-border)" }
                  : { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", border: "1px solid var(--oh-stat-green-border)" }}
              >
                {togglingActif ? <Loader2 className="w-3 h-3 animate-spin" /> : (inactif ? "● Inactif" : "○ Actif")}
              </button>
            );
          })()}

          <div className="flex-1" />

          {/* Action principale contextuelle */}
          {headerAction && !abandoned && (
            <Button size="sm" data-testid="header-action-btn"
              className="text-xs h-7 px-3"
              style={{ background: "rgba(59,130,246,0.15)", color: "var(--oh-stat-blue-text)", border: "1px solid rgba(59,130,246,0.3)" }}
              onClick={() => setActiveTab(headerAction.tab)}>
              <ArrowRight className="w-3 h-3 mr-1" />
              {headerAction.label}
            </Button>
          )}

          {/* Étape externe assumée : le dépôt/suivi réel se fait sur le portail
              officiel ANAH (monprojet.anah.gouv.fr). Lien secondaire, non impératif. */}
          {headerAction?.anahPortal && !abandoned && (
            <a href="https://monprojet.anah.gouv.fr" target="_blank" rel="noopener noreferrer"
              data-testid="anah-portal-link"
              className="inline-flex items-center text-xs h-7 px-3 rounded-md whitespace-nowrap"
              style={{ background: "transparent", color: "var(--oh-accent-text)", border: "1px solid var(--oh-accent)" }}
              title="Ouvre le portail officiel ANAH dans un nouvel onglet">
              <ExternalLink className="w-3 h-3 mr-1" />
              Continuer sur le portail officiel ANAH
            </a>
          )}

          <GovernanceIndicator dossierId={dossierId} tenantId={tenantId} onBlockedTransitions={setBlockedTransitions} />
          {/* BAT JJ — barre d'actions déplacée sur sa propre ligne pleine largeur (Row 1-bis ci-dessous). */}
        </div>

        {/* Row 1-bis — barre d'actions NOMMÉES sur sa propre ligne pleine largeur (BAT JJ :
            plus de boutons tassés/coupés à droite). Ordre : Documents · Commentaires · Message ·
            Historique · Sync Zoho · Analyse IA · Aide · Supprimer. Wrap propre, labels dès sm. */}
        <div className="flex items-center gap-1.5 px-4 pb-2 flex-wrap" data-testid="dossier-actions-bar"
          style={{ borderTop: "1px solid var(--oh-border)", paddingTop: "0.5rem" }}>
          <Button data-testid="documents-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowDocuments(true)} title="Documents du dossier" aria-label="Documents">
            <Files className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Documents{documents.length > 0 ? ` (${documents.length})` : ""}</span>
          </Button>
          <Button data-testid="commentaires-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowCommentaires(true)} title="Commentaires du dossier" aria-label="Commentaires">
            <MessageSquare className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Commentaires</span>
          </Button>
          <Button data-testid="send-message-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowSendMessage(true)} title="Envoyer un message (SMS/email) au client" aria-label="Envoyer un message">
            <Send className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Message</span>
          </Button>
          <Button data-testid="historique-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
            onClick={() => setShowHistorique(true)} title="Historique du dossier" aria-label="Historique">
            <Clock className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
            <span className="hidden sm:inline">Historique</span>
          </Button>
          {isTEF && (
            <Button data-testid="resync-zoho-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
              onClick={handleResyncZoho} disabled={syncingZoho}
              title="Resynchroniser les fichiers Zoho dans OmniHub" aria-label="Synchroniser Zoho">
              {syncingZoho
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--oh-accent-text)]" />
                : <FolderOpen className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />}
              <span className="hidden sm:inline">Sync Zoho</span>
            </Button>
          )}
          {isTEF && !abandoned && (
            <Button data-testid="launch-agent-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
              onClick={handleLaunchAgent} disabled={analyzingAgent} title="Analyse IA conformité" aria-label="Analyse IA">
              {analyzingAgent ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--oh-accent-text)]" /> : <Shield className="w-3.5 h-3.5 text-[var(--oh-accent-text)]" />}
              <span className="hidden sm:inline" style={{ color: "var(--oh-stat-blue-text, var(--oh-stat-blue-text))" }}>Analyse IA</span>
            </Button>
          )}
          {(user?.role || user?.rôle) !== "visiteur" && (
            <Button data-testid="btn-aide-contextuelle" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
              onClick={() => setShowTuto(true)} title="Aide contextuelle" aria-label="Aide">
              <HelpCircle className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
              <span className="hidden sm:inline">Aide</span>
            </Button>
          )}
          {isTEFRole && (
            <Button data-testid="delete-dossier-btn" size="sm" variant="ghost" className="text-xs h-7 px-2 gap-1.5"
              onClick={handleDeleteDossier} title="Supprimer le dossier" aria-label="Supprimer">
              <Trash2 className="w-3.5 h-3.5 text-[var(--oh-error-text)]" />
              <span className="hidden sm:inline text-[var(--oh-error-text)]">Supprimer</span>
            </Button>
          )}
        </div>

        {/* Agent progress banner */}
        {analyzingAgent && agentProgress && (
          <div className="flex items-center gap-3 px-4 py-1.5 text-xs" style={{ background: "rgba(59,130,246,0.08)", borderTop: "1px solid rgba(59,130,246,0.15)" }}>
            <Loader2 className="w-3 h-3 animate-spin text-[var(--oh-accent-text)]" />
            <span style={{ color: "var(--oh-stat-blue-text)" }}>{agentProgress}</span>
          </div>
        )}

        {/* Row 2: Pipeline Stepper */}
        <div className="px-4 py-1.5">
          <PipelineStepper statut={dossier.statut} compact dossier={dossier} />
        </div>

        {/* Row 2-bis : Dates par statut pipeline (S2 2026-05-28, collapsible) */}
        <details className="px-4 py-1.5" data-testid="dates-pipeline-collapsible">
          <summary className="text-xs cursor-pointer select-none" style={{ color: "var(--oh-text-muted)" }}>
            Dates par statut pipeline
          </summary>
          <div className="mt-2 max-w-2xl">
            <DatesPipelineBlock
              dossier={dossier}
              tenantId={tenantId}
              readOnly={!isTEFRole}
              onUpdated={loadDossier}
            />
          </div>
        </details>

        {/* Row 3: Onglets */}
        <div className="flex px-4 gap-0 overflow-x-auto" data-testid="dossier-tabs">
          {visibleTabs.map(tab => {
            const isActive = activeTab === tab.key;
            const TabIcon = tab.icon;
            const tc = tabCompletude[tab.key];
            const isComplete = tc && tc.filled === tc.total;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                title={tc ? (tc.missing?.length ? `À compléter : ${tc.missing.join(", ")}` : "Complet ✓") : undefined}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-all relative"
                style={{
                  color: isActive ? "var(--oh-text)" : "var(--oh-text-muted)",
                  borderBottom: isActive ? `3px solid ${tab.color}` : "3px solid transparent",
                }}
              >
                <TabIcon className="w-4 h-4" style={{ color: isActive ? tab.color : undefined }} />
                <span className="hidden sm:inline">{(!amo && tab.labelCore) ? tab.labelCore : tab.label}</span>
                <span className="sm:hidden">{((!amo && tab.labelCore) ? tab.labelCore : tab.label).slice(0, 5)}</span>
                {tc && (
                  <span
                    className="text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-md"
                    data-testid={`tab-completude-${tab.key}`}
                    style={{
                      background: isComplete ? "rgba(16,185,129,0.2)" : isActive ? `${tab.color}20` : "rgba(148,163,184,0.1)",
                      color: isComplete ? "#10b981" : isActive ? tab.color : "var(--oh-text-muted)",
                      border: `1px solid ${isComplete ? "rgba(16,185,129,0.3)" : isActive ? `${tab.color}30` : "transparent"}`,
                    }}
                  >
                    {tc.filled}/{tc.total}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Refonte P1 #3 : ce qui manque dans l'onglet actif (affichage clair) */}
        {(() => {
          const tcActive = tabCompletude[activeTab];
          if (!tcActive || !tcActive.missing?.length) return null;
          return (
            <div
              className="mx-4 mb-2 rounded-md px-3 py-1.5 flex items-center gap-2 text-xs"
              data-testid="tab-missing-hint"
              style={{
                background: "var(--oh-stat-amber-bg, rgba(245,158,11,0.1))",
                border: "1px solid var(--oh-stat-amber-border, rgba(245,158,11,0.25))",
                color: "var(--oh-stat-amber-text)",
              }}
            >
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>À compléter : <strong>{tcActive.missing.join(", ")}</strong></span>
            </div>
          );
        })()}
      </div>

      {/* Refonte P1.1 — AdminBlock (Bénéficiaire + Logement) n'est PLUS répété sur
          tous les onglets : déplacé dans l'onglet Infos uniquement (détail bénéficiaire).
          Le header condensé porte désormais ville·visiteur·installateur·conformité. */}

      {/* ═══ CONTENU ONGLET ═══ */}
      {/* PROTOTYPE SPLIT VIEW (design JJ) — les 5 onglets (Infos / Demande MPA /
          DAL / Finance / Conformité) sont désormais TOUS en master-detail :
          le frame s'étale PLEINE LARGEUR (plus de max-w-[1100px]/mx-auto),
          on garde juste un padding latéral de respiration. */}
      <div className="pt-4 space-y-4 w-full px-3 sm:px-5 lg:px-6">

        {/* ═══ ONGLET INFOS ═══ */}
        {activeTab === "infos" && (
          <div className="space-y-4" data-testid="tab-content-infos">
            {/* OMV France : fiche dédiée gérée par DossierOmvView (early-return ci-dessus),
                ne passe jamais par cet onglet TEF. */}

            {/* Onglet Infos refondu (spec docs/upload/mockup-infos.html). Blocs :
                Bénéficiaire / Logement / MaPrimeAdapt' / Compte ANAH.
                SPLIT VIEW (design JJ) : InfosTab est rendu TEL QUEL ; ses 4 BlockCards
                sont ancrés data-subnav-id, le menu gauche les montre un à un. Le
                toprow « Remplir depuis documents » + la note IA restent persistants. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_INFOS.filter(x => amo || !["mpa", "compte-anah"].includes(x.id)).map(x => (!amo && x.id === "beneficiaire") ? { ...x, label: "Client" } : x)} scanKey="infos" ariaLabel="Sections Infos">
              <InfosTab dossier={dossier} dossierId={dossierId} tenantId={tenantId} onUpdate={loadDossier} />
            </SplitViewLayout>

            {/* #5 (2026-06-21) — bloc « Documents & Photos » embarqué RETIRÉ d'Infos :
                doublon du bouton header « Documents » → panneau (qui couvre désormais
                Documents ET Photos, dépose + consulte). Accès conservé, zéro perte.
                Les sous-onglets métier Pièces/Préparer&Signer/Documents générés
                (DocumentsAidesWrapper, onglet Demande MPA) sont CONSERVÉS (fonction
                propre : génération + signature, distincte du simple dépôt). */}

            {/* CommercialSection déplacé vers l'onglet « Demande MPA » (Refonte P1.1, commit 2/3). */}
          </div>
        )}

        {/* ═══ ONGLET DEMANDE MPA (clé interne "operation") — Contrats & Engagement + Opération ═══ */}
        {activeTab === "operation" && (
          <div className="space-y-2.5" data-testid="tab-content-operation">
            {/* CC-DEV1-17 #1 — « À compléter » déplacé ici depuis l'onglet Infos
                (ACompleterBlock = source unique de la logique). Persistant au-dessus
                du split (guidance transversale, non rattaché à une section). */}
            <ACompleterBlock dossier={dossier} />

            {/* PROTOTYPE SPLIT VIEW (design JJ) — master-detail intra-onglet :
                menu sections à GAUCHE + contenu à DROITE. AUCUN contenu recréé :
                CommercialSection + OperationSection sont rendus tels quels, leurs
                blocs `data-subnav-id` (contrats / prestation-amo / visiteur /
                visite / installateur / financement / chantier) sont montrés un à un. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_OPERATION.filter(x => amo || x.id !== "prestation-amo")} scanKey={`op-${dossier.statut}`} ariaLabel={amo ? "Sections Demande MPA" : "Sections Opération"}>
              {/* Refonte P1.1 — Contrats & Engagement (Devis/Contrat AMO + choix visiteur + DocuSign) :
                  CommercialSection déplacé ENTIER depuis l'onglet Infos (zéro re-découpage interne). */}
              <CommercialSection dossier={dossier} dossierId={dossierId} tenantId={tenantId} isTEF={isTEF} abandoned={abandoned} onUpdate={loadDossier} onNavigate={onNavigate} />

              <OperationSection dossier={dossier} dossierId={dossierId} tenantId={tenantId} isTEF={isTEF} abandoned={abandoned} onUpdate={loadDossier}>
                {/* Chantier — rendu UNIQUEMENT en phase travaux. Le div lui-même est
                    conditionnel (et non un contenu vide dans un div toujours présent) :
                    ainsi SplitViewLayout ne détecte pas le bloc hors phase travaux et
                    RETIRE automatiquement l'entrée « Chantier » du menu (plus de section
                    vide). En phase travaux, la section réapparaît avec son contenu. */}
                {["chantier_a_planifier","chantier_planifie","chantier_en_cours","chantier_termine","travaux_planifies","travaux_en_cours","travaux_termines"].includes(dossier.statut) && (
                  <div data-subnav-id="chantier">
                    <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                      <PlanningChantier dossierId={dossier.id} tenantId={tenantId} dossier={dossier} />
                    </div>
                    <div className="rounded-lg p-4 mt-4" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                      <CommandesManager dossierId={dossier.id} tenantId={tenantId} devisSigne={["signe","valide","accepte","en_commande","travaux_en_cours","travaux_termines"].includes(dossier.statut)} />
                    </div>
                    <PhotosChantier dossierId={dossier.id} tenantId={tenantId} verrouille={dossier.verrouille || abandoned} clientNom={dossier.client?.nom || ""} dossierRef={dossier.référence} />
                  </div>
                )}
              </OperationSection>
            </SplitViewLayout>

            {/* Contextual hints */}
            {pipelineStep === "dossier_cree" && (
              <div className="rounded-lg p-3 flex items-center gap-2 text-xs" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)", color: "var(--oh-stat-blue-text)" }}>
                <ArrowRight className="w-4 h-4 flex-shrink-0" />
                Prochaine étape : planifier la visite AMO et assigner un visiteur
              </div>
            )}
            {pipelineStep === "visite_planifiee" && (
              <div className="rounded-lg p-3 flex items-center gap-2 text-xs" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)", color: "var(--oh-stat-blue-text)" }}>
                <ArrowRight className="w-4 h-4 flex-shrink-0" />
                Visite planifiée. Envoyer l'accès DAL Mobile au visiteur.
              </div>
            )}
          </div>
        )}

        {/* ═══ ONGLET DAL (refonte P1 : sorti du <details> Conformité → visible) ═══ */}
        {activeTab === "dal" && isTEF && (
          <div className="space-y-4" data-testid="tab-content-dal">
            {/* PROTOTYPE SPLIT VIEW (design JJ) — master-detail intra-onglet DAL.
                AUCUN contenu recréé : DALMobileBlock / DesktopDALWizard (wizard
                partagé qui se navigue étape par étape À DROITE via son propre
                stepper) / DALForm (vue classique) sont rendus tels quels, chacun
                enveloppé d'un `data-subnav-id` pour le pilotage gauche/droite. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_DAL} scanKey="dal" ariaLabel="Sections DAL" onNavigateRef={dalNavRef}>
              {/* CC-DEV1-17 #2 — DAL Mobile (saisie terrain). Fonction intacte. */}
              <div data-subnav-id="dal-mobile">
                <DALMobileBlock dossier={dossier} dossierId={dossierId} tenantId={tenantId} showEmptyState />
              </div>

              {/* CC-AUDIT-43 L2 — Assistant DAL = wizard PARTAGÉ (même composant que la
                  PWA mobile). Il conserve son propre stepper (Besoins → Zones →
                  Solutions → Préconisations → Générer) rendu à DROITE ; reprise de la
                  saisie via initialData (GET DAL), autosave session desktop. */}
              <div data-subnav-id="dal-wizard" data-testid="dal-wizard-section">
                <DesktopDALWizard dossier={dossier} dossierId={dossierId} tenantId={tenantId} onUpdate={loadDossier} />
              </div>

              {/* "DAL" = CONSULTATION (V1.1) : tableau de bord LECTURE SEULE + barre
                  d'actions (Générer / Télécharger, pas d'Imprimer). La saisie se fait
                  dans "Assistant DAL". Source unique = même GET .../dal. DALForm reste
                  dans le code (filet) mais n'est plus monté ici. */}
              <div data-subnav-id="dal-classique" data-testid="dal-vue-classique">
                <DALConsultation dossierId={dossierId} reference={dossier?.reference} tenantId={tenantId} client={dossier?.client} infosLogement={dossier?.logement || dossier?.infos_logement} onUpdate={loadDossier} onEdit={() => dalNavRef.current?.("dal-wizard")} />
              </div>
            </SplitViewLayout>
          </div>
        )}

        {/* ═══ ONGLET CONFORMITÉ ═══ */}
        {activeTab === "visites" && (
          <VisitesDuDossier dossierId={dossierId} tenantId={tenantId} />
        )}

        {activeTab === "conformite" && (
          <div className="space-y-4" data-testid="tab-content-conformite">
            {/* SPLIT VIEW (design JJ) — 2 sections cohérentes : Liaison ANAH (trigger
                modale) + Vérification Conformité (ConformiteEditableTab ENTIER, qui
                garde son header/save/progress + ses accordéons internes Dates ANAH /
                Checklist pièces / Plan / Détail). Une découpe plus fine imposerait de
                recréer le header partagé du composant → refusé (O1). */}
            <SplitViewLayout sections={SPLIT_SECTIONS_CONFORMITE} scanKey="conformite" ariaLabel="Sections Conformité">
            {/* Liaison ANAH — données importées (lecture seule), INLINE dans le panneau
                droit du split (cohérence avec les autres sections ; plus de modale).
                Source unique ANAH inchangée : on réutilise tel quel AnahLinkTab +
                DonneesMPR (O1, même contenu que l'ancienne modale, rendu inline). */}
            {isTEF && (
              <div data-subnav-id="liaison-anah" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="anah-inline-section">
                <details open>
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                    <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                      <Shield className="w-4 h-4 text-[var(--oh-success-text)]" /> Liaison ANAH
                      {(dossier?.anah_reference || dossier?.reference_anah)
                        ? <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>— Réf. {dossier.anah_reference || dossier.reference_anah}</span>
                        : <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>— non liée</span>}
                      {dossier?.statut_anah && <span className="text-xs font-normal" style={{ color: "var(--oh-text-muted)" }}>· statut {dossier.statut_anah}</span>}
                    </span>
                    <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                  </summary>
                  <div className="px-4 pb-4">
                    {/* Bloc « Dossier ANAH » (DonneesMPA) RETIRÉ (décision CTO) : doublon
                        avec Liaison ANAH, enrichi ci-dessus (numéro, statut, dates création/
                        dépôt/acceptation, montant accordé). Le mandataire se gère en Demande
                        MPA (liste) + Configuration Mandataire ANAH. */}
                    <AnahLinkTab dossierId={dossierId} tenantId={tenantId} readOnly={dossier?.verrouille || abandoned} />
                  </div>
                </details>
              </div>
            )}

            {/* Conformité editable — open par défaut : action principale de Stéphanie (dépôt ANAH) */}
            {/* Plan de financement MPA (CERFA 13460-03) — SECTION PROPRE du split
                Conformité (pièce du dépôt ANAH). Bouton Télécharger PERSISTANT +
                Régénérer (détection du doc généré via /devis-contrats). Sorti du slot
                planTrigger de ConformiteEditableTab où il était noyé. */}
            <PlanFinancementBlock dossier={dossier} dossierId={dossierId} tenantId={tenantId} onEdit={() => setShowPlanModal(true)} />

            {/* Aides complémentaires (module AG-05) — remonté dans Conformité (aux côtés
                du Plan de financement) et NON dans Finance : l'onglet Finance reste
                strictement Facture AMO (revenus TEF), cf. CLAUDE.md. Composant + backend
                /api/aides existants réutilisés tels quels (O1). Gate isTEF. */}
            {isTEF && (
              <div data-subnav-id="aides-complementaires" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }} data-testid="bloc-aides-complementaires">
                <details open>
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                    <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                      <HandCoins className="w-4 h-4" style={{ color: "var(--oh-hub-aides, var(--oh-stat-cyan-text))" }} /> Aides complémentaires
                    </span>
                    <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                  </summary>
                  <div className="px-4 pb-4">
                    <AidesComplementairesTab dossierId={dossierId} tenantId={tenantId} dossier={dossier} onUpdate={loadDossier} />
                  </div>
                </details>
              </div>
            )}

            <div data-subnav-id="verification" className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
              <details open>
                <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                  <span className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text)" }}>
                    <CheckCircle2 className="w-4 h-4 text-[var(--oh-info-text)]" /> Vérification Conformité
                  </span>
                  <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
                </summary>
                <div className="px-4 pb-4">
                  <ConformiteEditableTab dossier={dossier} tenantId={tenantId} isTEF={isTEF} onUpdate={loadDossier} />
                </div>
              </details>
            </div>
            </SplitViewLayout>

            {/* Contextual hints (persistants sous le split) */}
            {pipelineStep === "visite_faite" && (
              <div className="rounded-lg p-3 flex items-center gap-2 text-xs" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.15)", color: "var(--oh-stat-amber-text)" }}>
                <ArrowRight className="w-4 h-4 flex-shrink-0" />
                Visite effectuée. Compléter le DAL et préparer le plan de financement avant dépôt.
              </div>
            )}
            {pipelineStep === "pret_depot" && (
              <div className="rounded-lg p-3 flex items-center gap-2 text-xs" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.15)", color: "var(--oh-stat-green-text)" }}>
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Dossier prêt pour le dépôt ANAH. Vérifiez la checklist de complétude.
              </div>
            )}
          </div>
        )}

        {/* ═══ ONGLET FINANCE ═══ */}
        {activeTab === "finance" && effectiveRole === "tef" && (
          <div className="space-y-4" data-testid="tab-content-finance">
            {/* Finance TEF — SPLIT VIEW (design JJ) : FinanceSection rendu TEL QUEL,
                ses 3 blocs (facture-amo / paiement / paiement-visiteur) sont DÉJÀ
                ancrés data-subnav-id, le menu gauche les montre un à un. */}
            <SplitViewLayout sections={SPLIT_SECTIONS_FINANCE} scanKey="finance" ariaLabel="Sections Finance">
              <FinanceSection dossier={dossier} dossierId={dossierId} tenantId={tenantId} isTEF={isTEF} abandoned={abandoned} onUpdate={loadDossier} />
            </SplitViewLayout>
          </div>
        )}

        {/* Refonte P1.1 — bloc « Workflow avancé » répété en bas retiré
            (le pipeline + l'action contextuelle du header couvrent la transition de statut). */}
      </div>

      {/* ═══ Modals ═══ */}
      {/* Refonte P1.1 — panneau Documents (réutilise DocumentsForm ; PAS la modale
          multicanal complète, prévue en P1.2). */}
      <Dialog open={showDocuments} onOpenChange={setShowDocuments}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Documents &amp; Photos — {dossierRef || clientDisplay}</DialogTitle></DialogHeader>
          {/* #5 — le panneau couvre désormais Documents ET Photos (dépose + consulte),
              pour conserver l'accès aux Photos après retrait du bloc embarqué d'Infos. */}
          <div className="space-y-4">
            <DocumentsForm dossierId={dossierId} tenantId={dossier?.tenant_id || tenantId} documents={documents} verrouille={dossier.verrouille || abandoned} onUpdate={loadDossier} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
            <PhotosForm dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille || abandoned} onUpdate={loadDossier} clientNom={dossier.client_nom} dossierRef={dossier.référence} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Refonte P1.1 — panneau Commentaires (réutilise CommentairesDossier, exposé
          depuis le header : bouton/panneau, plus un bloc en bas de page). */}
      <Dialog open={showCommentaires} onOpenChange={setShowCommentaires}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Commentaires — {dossierRef || clientDisplay}</DialogTitle></DialogHeader>
          <CommentairesDossier dossierId={dossierId} tenantId={tenantId} verrouille={dossier.verrouille || abandoned} />
        </DialogContent>
      </Dialog>

      {/* Liaison ANAH : plus de modale — le contenu (AnahLinkTab + DonneesMPR) est
          désormais rendu INLINE dans le panneau droit du split Conformité (cohérence). */}

      {/* Conformité — MODALE Plan de financement CERFA (réutilise FinancementTab). */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="modal-plan">
          <DialogHeader><DialogTitle>Plan de financement MPA — CERFA 13460-03 — {dossierRef || clientDisplay}</DialogTitle></DialogHeader>
          <FinancementTab dossierId={dossierId} tenantId={tenantId} dossier={dossier} onUpdate={loadDossier} />
        </DialogContent>
      </Dialog>

      {showHistorique && <HistoriqueModal historique={historique} onClose={() => setShowHistorique(false)} />}
      <SendMessageModal
        open={showSendMessage}
        onClose={() => setShowSendMessage(false)}
        dossier={dossier}
        tenantId={tenantId}
        currentUser={user}
      />

      <TutoModal
        slug={(() => {
          const MAPPING_STEPHANIE = {
            prospect: "tuto-stephanie-01-creer-dossier",
            dossier_cree: "tuto-stephanie-01-creer-dossier",
            visite_planifiee: "tuto-stephanie-04-planifier-visite",
            visite_faite: "tuto-stephanie-04-planifier-visite",
            pret_depot: "tuto-stephanie-07-documents-anah",
            déposé: "tuto-stephanie-09-deposer-dossier",
            dossier_depose: "tuto-stephanie-09-deposer-dossier",
            instruction: "tuto-stephanie-09-deposer-dossier",
            aides_en_instruction: "tuto-stephanie-09-deposer-dossier",
            octroi: "tuto-stephanie-09-deposer-dossier",
            aides_validees: "tuto-stephanie-09-deposer-dossier",
            travaux: "tuto-stephanie-09-deposer-dossier",
            clôture: "tuto-stephanie-03-completer-dossier",
            paye: "tuto-stephanie-03-completer-dossier",
            archive: "tuto-stephanie-03-completer-dossier",
          };
          const MAPPING_INSTALLATEUR = {
            prospect: "tuto-installateur-02-voir-dossiers",
            dossier_cree: "tuto-installateur-02-voir-dossiers",
            visite_planifiee: "tuto-installateur-02-voir-dossiers",
            visite_faite: "tuto-installateur-03-ajouter-devis",
            pret_depot: "tuto-installateur-04-ajouter-facture",
            déposé: "tuto-installateur-05-photos-chantier",
            dossier_depose: "tuto-installateur-05-photos-chantier",
            instruction: "tuto-installateur-06-suivre-avancement",
            aides_en_instruction: "tuto-installateur-06-suivre-avancement",
            octroi: "tuto-installateur-04-ajouter-facture",
            aides_validees: "tuto-installateur-04-ajouter-facture",
            travaux: "tuto-installateur-05-photos-chantier",
            clôture: "tuto-installateur-06-suivre-avancement",
            paye: "tuto-installateur-06-suivre-avancement",
            archive: "tuto-installateur-06-suivre-avancement",
          };
          const mapping = (user?.role || user?.rôle) === "installateur" ? MAPPING_INSTALLATEUR : MAPPING_STEPHANIE;
          const step = pipelineStep || dossier?.statut;
          return mapping[step] || ((user?.role || user?.rôle) === "installateur" ? "tuto-installateur-02-voir-dossiers" : "tuto-stephanie-01-creer-dossier");
        })()}
        isOpen={showTuto}
        onClose={() => setShowTuto(false)}
        tenantId={tenantId}
      />
    </div>
  );
}

// 🔴 LES VISITES DU DOSSIER — la relation existait, l'écran la rendait invisible.
//
// ⚠️ MESURÉ le 18/09 : la visite porte `dossier_id` et `dossier_reference`, le
// devis porte `visite_id`, la visite porte `devis_id`. La chaîne est COMPLÈTE
// et BIDIRECTIONNELLE en base — mais AUCUN écran ⛔ ne permettait d'aller du
// dossier à sa visite, donc ⛔ ni à son RAPPORT. « Dossier → Visite → Rapport »
// était impossible par l'interface, alors que la donnée était là.
// 🔑 CE COMPOSANT LIT ET AFFICHE. ⛔ Il ⛔ ne CRÉE rien, ⛔ ne RATTACHE rien,
// ⛔ ne DUPLIQUE aucun écran de rapport : le rapport est servi par la route qui
// existe déjà.
function VisitesDuDossier({ dossierId, tenantId }) {
  const [visites, setVisites] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/visite/saisies?dossier_id=${encodeURIComponent(dossierId)}`,
          { headers: getAuthHeaders(tenantId), credentials: "include" });
        if (!res.ok) throw new Error(`Erreur ${res.status}`);
        const d = await res.json();
        setVisites(d.en_cours || []);
      } catch (e) { setErreur(e.message); }
    })();
  }, [dossierId, tenantId]);

  if (erreur) {
    return (
      <div className="p-4 rounded-lg" data-testid="visites-erreur"
           style={{ background: "var(--oh-stat-red-bg)",
                    color: "var(--oh-stat-red-text)" }}>{erreur}</div>
    );
  }
  if (visites === null) {
    return <div className="p-4 text-[var(--oh-text-muted)]">Chargement…</div>;
  }
  if (!visites.length) {
    // ⛔ Un écran vide sans phrase se lit comme une panne. On DIT l'état.
    return (
      <div className="p-6 text-center" data-testid="visites-aucune">
        <p style={{ color: "var(--oh-text)" }}>Aucune visite sur ce dossier.</p>
        <p className="text-sm mt-1" style={{ color: "var(--oh-text-secondary)" }}>
          Les visites se créent depuis l'application terrain.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="visites-liste">
      {visites.map((v) => (
        <div key={v.id} data-testid="visite-ligne"
             className="p-4 rounded-lg border"
             style={{ background: "var(--oh-bg-elevated)",
                      borderColor: "var(--oh-border)" }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p style={{ color: "var(--oh-text)", fontWeight: 500 }}>
                {v.profil_code}
              </p>
              <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
                {(v.created_at || "").slice(0, 10)} · {v.nb_reponses} réponse(s)
                · {v.nb_observations} observation(s)
                {v.nb_bloquantes ? ` · ${v.nb_bloquantes} réserve(s) bloquante(s)` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 rounded"
                    data-testid="visite-statut"
                    style={{ background: v.statut === "terminee"
                               ? "var(--oh-stat-green-bg)" : "var(--oh-stat-amber-bg)",
                             color: v.statut === "terminee"
                               ? "var(--oh-stat-green-text)" : "var(--oh-stat-amber-text)" }}>
                {v.statut === "terminee" ? "Clôturée" : "En cours"}
              </span>
              {/* 🔑 LE RAPPORT — la route existe déjà, ⛔ on n'en crée pas une. */}
              <a href={`${API}/api/visite/saisies/${v.id}/rapport`}
                 target="_blank" rel="noreferrer" data-testid="visite-rapport"
                 className="text-sm underline"
                 style={{ color: "var(--oh-accent-text)" }}>
                {v.statut === "terminee" ? "Rapport" : "Rapport provisoire"}
              </a>
              {/* 🔑 LA RELATION AVEC LE DEVIS, CONSTATÉE — ⛔ pas fabriquée. */}
              {v.devis_id && (
                <span className="text-xs" data-testid="visite-devis"
                      style={{ color: "var(--oh-text-secondary)" }}>
                  · a produit un devis
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
